import { api } from '@/features/auth';
import { transformNotifications } from '../helpers';
import { CreateNotificationsPayload, NotificationRaw } from '../types';


export const notificationsApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getUserNotifications: builder.query({
            query: ({ userId }) => {
                const params = new URLSearchParams({
                    'filter[user][_eq]': userId,
                    'sort': '-date_created',
                    'limit': '100',
                });
                return {
                    url: `items/notifications?${params.toString()}`,
                    method: 'GET',
                };
            },
            transformResponse: (response: { data: NotificationRaw[] }) => {
                return transformNotifications(response.data);
            },
            providesTags: (result, error, { userId }) => [
                { type: 'Notifications', id: 'LIST' },
                { type: 'Notifications', id: userId },
            ],
        }),
        archiveNotification: builder.mutation<Notification, { notificationId: string; userId: string }>({
            query: ({ notificationId }) => ({
                url: `/items/notifications/${notificationId}`,
                method: 'PATCH',
                body: {
                    status: 'archived',
                },
            }),
            invalidatesTags: (result, error, { userId }) => [
                { type: 'Notifications', id: 'LIST' },
                { type: 'Notifications', id: userId },
                { type: 'UnreadCount', id: userId },
            ],
        }),
        markAsRead: builder.mutation<Notification, { notificationId: string; userId: string }>({
            query: ({ notificationId }) => ({
                url: `/items/notifications/${notificationId}`,
                method: 'PATCH',
                body: {
                    is_read: true,
                    read_at: new Date().toISOString(),
                    status: 'read',
                },
            }),
            invalidatesTags: (result, error, { userId }) => [
                { type: 'Notifications', id: 'LIST' },
                { type: 'Notifications', id: userId },
                { type: 'UnreadCount', id: userId },
            ],
        }),
        createNotifications: builder.mutation<Notification[], CreateNotificationsPayload>({
            query: ({ userIds, type, title, relatedEntity, priority = 'low', actionUrl = null }) => {
                const items = userIds.map((userId) => ({
                    status: 'unread',
                    user: userId,
                    type,
                    title,
                    related_entity: relatedEntity,
                    action_url: actionUrl,
                    priority,
                    is_read: false
                }));

                return {
                    url: `/items/notifications`,
                    method: 'POST',
                    body: { items },
                };
            },
            transformResponse: (response: { data: NotificationRaw[] | NotificationRaw }): any => {
                const dataArray = Array.isArray(response.data)
                    ? response.data
                    : [response.data];

                return transformNotifications(dataArray);
            },
            invalidatesTags: (result, error, { userIds }) => [
                { type: 'Notifications', id: 'LIST' },
                ...userIds.map((id) => ({ type: 'Notifications' as const, id })),
                ...userIds.map((id) => ({ type: 'UnreadCount' as const, id })),
            ],
        }),
    })
});

export const {
    useGetUserNotificationsQuery,
    useArchiveNotificationMutation,
    useMarkAsReadMutation,
    useCreateNotificationsMutation
} = notificationsApi;
